import {
  Component,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonContent, ModalController, Platform, PopoverController } from '@ionic/angular';
import { PopoverSocialShareComponent } from 'src/app/components/popover-social-share/popover-social-share.component';
import { SeoService } from 'src/app/services/seo.service';
import { SEOSchema } from 'src/app/types/seo';
import { environment } from 'src/environments/environment';
import { JsonLdService } from 'src/app/services/json-ld.service';
import { PostItem } from 'src/app/types/post';
import { isPlatformBrowser } from '@angular/common';
import { ButtonIcon, IconPosition } from 'src/app/components/button/button.component';
import { Icon } from 'src/app/components/icons/svg-icon.component';
import { MappedinComponent } from 'src/app/components/mappedin/mappedin.component';

@Component({
  selector: 'app-store-detail',
  templateUrl: './store-detail.page.html',
  styleUrls: ['./store-detail.page.scss'],
})
export class StoreDetailPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  cordova: boolean = false;
  desktop: boolean = false;
  tablet: boolean = false;
  mobile: boolean = false;
  xhrInProgress: boolean = false;
  showMap: boolean = false;
  isBrowser: boolean = false;
  amenities;
  dietaryPreferences;
  services;
  paymentOptions;
  currentDayName: string;
  websiteUrl: string;
  environmentName: string = environment.environment_title;

  mapIcon: ButtonIcon = {
    name: Icon.Map,
    position: IconPosition.LEFT,
  };

  directionsIcon: ButtonIcon = {
    name: Icon.Walking,
    position: IconPosition.LEFT,
  }

  emailIcon: ButtonIcon = {
    name: Icon.Mail,
    position: IconPosition.LEFT,
  }

  phoneIcon: ButtonIcon = {
    name: Icon.Phone,
    position: IconPosition.LEFT,
  }

  websiteBookingIcon: ButtonIcon = {
    name: Icon.ExternalLink,
    position: IconPosition.LEFT,
  }

  data: PostItem;

  schema: SEOSchema = {
    '@context': 'http://schema.org',
    '@type': 'Store',
    '@id': '',
    url:
      typeof window !== 'undefined' ? window.location.href.split('#')[0] : null,
    priceRange: 'ZAR',
  };

  slideOpts = {
    freeMode: false,
    pagination: false,
    breakpoints: {
      280: {
        slidesPerView: 1.2,
        spaceBetween: 20,
      },
      768: {
        slidesPerView: 2.4,
        spaceBetween: 40,
      },
      1024: {
        slidesPerView: 3.33,
        spaceBetween: 40,
      },
    },
  };

  constructor(
    private platform: Platform,
    private seo: SeoService,
    private route: ActivatedRoute,
    private router: Router,
    public popoverController: PopoverController,
    public modalController: ModalController,
    private jsonLdService: JsonLdService,
    @Inject(PLATFORM_ID) platformId: string
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    this.currentDayName = new Date()
      .toLocaleDateString('en-US', {
        weekday: 'long',
      })
      .toLowerCase();

    this.platform.ready().then(() => {
      this.cordova = this.platform.is('cordova');
      this.desktop =
        this.platform.is('desktop') ||
        (typeof window !== 'undefined' && window.innerWidth > 1200);
      this.tablet = this.platform.is('tablet');
      this.mobile = this.platform.is('mobile') || (typeof window !== 'undefined' && window.innerWidth < 768);
    });

    this.platform.resize.subscribe(async () => {
      this.desktop =
        this.platform.is('desktop') ||
        (typeof window !== 'undefined' && window.innerWidth > 1200);
      this.mobile = this.platform.is('mobile') || (typeof window !== 'undefined' && window.innerWidth < 768);
    });
  }

  /**
   * Link to store detail
   */
  linkToDetail(item) {
    this.router.navigateByUrl(`/shop/${item.slug}`);
  }

  /**
   * Present social popover
   */
  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: PopoverSocialShareComponent,
      event: ev,
      translucent: true,
      cssClass: 'social-popover',
    });
    return await popover.present();
  }

  /**
   * Find and set store opening to closing time
   */
  storeOpenClose(item) {
    if (item.typeTrading && item.typeTrading[0]) {
      for (let index = 0; index < item.typeTrading.length; index++) {
        const element = item.typeTrading[index];
        if (element.dayName == this.currentDayName) {
          return element.open_text + ' - ' + element.close_text;
          break;
        }
      }
    } else if (item.typeTrading[this.currentDayName]) {
      return (
        item.typeTrading[this.currentDayName].open_text +
        ' - ' +
        item.typeTrading[this.currentDayName].close_text
      );
    }
  }

  ngOnInit() {
    this.route.data.subscribe((response: any) => {
      if (typeof window !== 'undefined') {
        window.addEventListener('popstate', (event) => {
          if (event.state) {
            window.location.replace('/shops');
          }
        });
      }

      if (typeof response.data.store.category === 'string') {
        response.data.store.category = JSON.parse(response.category);
      }

      if (response.data.services) {
        response.data.services.map((section) => {
          if (section.slug === 'amenities') {
            this.amenities = section.children;
          }

          if (
            section.slug.includes('food') &&
            section.slug.includes('beverage')
          ) {
            this.dietaryPreferences = section.children;
          }

          if (section.slug === 'services') {
            this.services = section.children;
          }

          if (
            section.slug.includes('payment') &&
            section.slug.includes('options')
          ) {
            this.paymentOptions = section.children;
          }
        });
      }

      this.data = response.data;

      if (this.data.store?.typeGeneral?.website) {
        if (this.data.store.typeGeneral.website.indexOf('://') < 0) {
          this.websiteUrl = '//' + this.data.store.typeGeneral.website;
        } else {
          this.websiteUrl = this.data.store.typeGeneral.website;
        }
      }

      // Set Meta & Social tags
      if (!this.cordova) {
        let schemaData = { ...this.schema };

        if (this.data?.store) {
          // Schema image
          if (this.data.store.logoImage) {
            schemaData = {
              ...schemaData,
              image: this.data.store.logoImage,
            };
          } else if (
            this.data.store.social.socialImage ||
            this.data.store.socialImage
          ) {
            schemaData = {
              ...schemaData,
              image:
                this.data.store.social.socialImage ||
                this.data.store.socialImage,
            };
          }

          // Telephone number
          if (this.data.store.typeGeneral?.telephone_number) {
            schemaData = {
              ...schemaData,
              telephone: this.data.store.typeGeneral.telephone_number
                .replace(')', '')
                .replace('(', ''),
            };
          }

          // Title tag
          if (this.data.store.seo?.title) {
            this.seo.setTitle(this.data.store.seo.title);
          } else {
            this.seo.setTitle(this.data.store.title);
          }

          // Schema title
          schemaData = {
            ...schemaData,
            name: this.data.store.title,
          };

          // Meta tags
          if (this.data.store.seo) {
            this.seo.setMeta(this.data.store.seo);
            this.seo.setRobotsMeta(this.data.store.seo);

            // Canonical URL
            if (this.data.store.seo.canonical_url) {
              this.seo.setCanonicalURL(this.data.store.seo);
            }
          }

          // Social tags
          if (this.data.store.social) {
            if (
              !this.data.store.social.socialImage &&
              this.data.store.socialImage
            )
              this.data.store.social.socialImage = this.data.store.socialImage;

            this.seo.setSEOMeta(this.data.store.social);
          }

          // Author
          if (this.data.store.createdBy) {
            this.seo.setAuthor(this.data.store.createdBy);
          }

          // Schema Trading Times
          if (this.data.store.typeTrading) {
            let tradingTimes = [];

            this.data.store.typeTrading.forEach((day) => {
              if (day.open) {
                tradingTimes.push(
                  day.dayName.charAt(0).toUpperCase() + day.dayName.slice(1)
                );
              }
            });

            if (tradingTimes.length > 0)
              schemaData = {
                ...schemaData,
                openingHoursSpecification: {
                  '@type': 'OpeningHoursSpecification',
                  dayOfWeek: tradingTimes,
                  opens: this.data.store.typeTrading[1].open_text,
                  closes: this.data.store.typeTrading[1].close_text,
                },
              };
          }

          // Schema Address
          schemaData = {
            ...schemaData,
            address: {
              '@type': 'PostalAddress',
              streetAddress: this.data.store.typeGeneral.store_number,
              addressLocality: environment.address_locality,
              postalCode: environment.postal_code,
              addressCountry: environment.address_country,
            },
          };

          this.schema = { ...this.schema, ...schemaData };
          this.jsonLdService.insertSchema(this.schema);
        }
      }

      this.xhrInProgress = false;
    });
  }

  /**
   * Toggle search modal
   */
  async presentMapModal(forDirection: boolean = false) {
    if(forDirection) {
        window.location.hash = '/directions?to=' + this.data.store.slug + '&isEditing=true';
    } else {
        window.location.hash = '/profile?location=' + this.data.store.slug;
    }
    const modal = await this.modalController.create({
      component: MappedinComponent,
      cssClass: 'floating',
      componentProps: {
        popupView: true
      }
    });
    return await modal.present();
  }

  getUpcomingDays() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return (
      this.data?.store.typeDays?.filter((day) => {
        const dayDate = new Date(day.date);
        dayDate.setHours(0, 0, 0, 0);
        return dayDate >= today;
      }) || []
    );
  }

  ionViewWillEnter() {
    this.content.scrollToTop();
  }

  ionViewWillLeave() {
    this.showMap = false;

    if (this.isBrowser && document.getElementById('mappedin-wrapper'))
      document.getElementById('mappedin-wrapper').remove();
  }
}
